import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Ticket,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  Copy,
  Eye,
  EyeOff,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Loader2,
  Percent,
  Clock,
  ChevronDown,
  Save
} from 'lucide-react';

const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://api.eventhouse.in';
// const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://styles-questions-ver-mat.trycloudflare.com';

const Coupon = () => {
  // State Management
  const [coupons, setCoupons] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [stats, setStats] = useState(null);
  const [notification, setNotification] = useState(null);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [limit] = useState(10);

  // Statistics State
  const [statsData, setStatsData] = useState({
    total: 0,
    active: 0,
    expired: 0,
    totalUsage: 0
  });

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    event: '',
    discountType: 'FIXED',
    discountValue: '',
    minPurchaseAmount: 0,
    maxDiscountAmount: '',
    usageLimit: '',
    userUsageLimit: 1,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    isActive: true
  });

  // Fetch Events on mount
  useEffect(() => {
    fetchEvents();
    fetchStatistics();
  }, []);

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1);
      fetchCoupons();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Fetch when filters or page changes
  useEffect(() => {
    fetchCoupons();
  }, [currentPage, filterStatus, filterType]);

  // UPDATED: Fetch Events
  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/v1/public/events`, {
        params: { limit: 1000 }, // Get all events
        withCredentials: true,
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setEvents(response.data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      showNotification('error', 'Failed to load events');
    }
  };

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit,
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(filterType !== 'all' && { discountType: filterType })
      };

      const response = await axios.get(`${baseURL}/api/v1/admin/coupon/all`, {
        params,
        withCredentials: true,
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setCoupons(response.data.coupons || []);
        setTotalPages(response.data.pagination?.pages || 1);
        setTotalCoupons(response.data.pagination?.total || 0);
      }
    } catch (error) {
      showNotification('error', 'Failed to fetch coupons');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/v1/admin/coupon/all`, {
        params: { limit: 1000 },
        withCredentials: true,
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const allCoupons = response.data.coupons || [];
        const total = allCoupons.length;
        const active = allCoupons.filter((c) => c.isActive && !c.isExpired).length;
        const expired = allCoupons.filter((c) => c.isExpired).length;
        const totalUsage = allCoupons.reduce((sum, c) => sum + (c.usageCount || 0), 0);
        
        setStatsData({ total, active, expired, totalUsage });
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  // NEW: Handle event selection and auto-fill validUntil
  const handleEventChange = (eventId) => {
    setFormData(prev => ({ ...prev, event: eventId }));
    
    if (eventId) {
      const selectedEvent = events.find(e => e._id === eventId);
      if (selectedEvent && selectedEvent.date) {
        // Convert event date to YYYY-MM-DD format for date input
        const eventEndDate = new Date(selectedEvent.date);
        const formattedDate = eventEndDate.toISOString().split('T')[0];
        
        setFormData(prev => ({
          ...prev,
          event: eventId,
          validUntil: formattedDate
        }));
      }
    }
  };

  // Create/Update Coupon
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event) {
      showNotification('error', 'Please select an event');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        discountValue: parseFloat(formData.discountValue),
        minPurchaseAmount: parseFloat(formData.minPurchaseAmount) || 0,
        maxDiscountAmount: formData.maxDiscountAmount
          ? parseFloat(formData.maxDiscountAmount)
          : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        userUsageLimit: parseInt(formData.userUsageLimit) || 1
      };

      const url = selectedCoupon
        ? `${baseURL}/api/v1/admin/coupon/${selectedCoupon._id}`
        : `${baseURL}/api/v1/admin/coupon/create`;

      const method = selectedCoupon ? 'put' : 'post';

      const response = await axios[method](url, payload, {
        withCredentials: true,
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        showNotification(
          'success',
          selectedCoupon ? 'Coupon updated successfully' : 'Coupon created successfully'
        );
        setIsModalOpen(false);
        resetForm();
        fetchCoupons();
        fetchStatistics();
      }
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Delete Coupon
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const response = await axios.delete(`${baseURL}/api/v1/admin/coupon/${id}`, {
        withCredentials: true,
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        showNotification('success', 'Coupon deleted successfully');
        fetchCoupons();
        fetchStatistics();
      }
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Failed to delete coupon');
    }
  };

  // Toggle Status
  const handleToggleStatus = async (id) => {
    try {
      const response = await axios.patch(
        `${baseURL}/api/v1/admin/coupon/${id}/toggle-status`,
        {},
        {
          withCredentials: true,
          headers: {
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        showNotification('success', response.data.message);
        fetchCoupons();
        fetchStatistics();
      }
    } catch (error) {
      showNotification('error', 'Failed to toggle status');
    }
  };

  // View Stats
  const handleViewStats = async (id) => {
    try {
      const response = await axios.get(`${baseURL}/api/v1/admin/coupon/${id}/stats`, {
        withCredentials: true,
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setStats(response.data.stats);
        setIsStatsModalOpen(true);
      }
    } catch (error) {
      showNotification('error', 'Failed to fetch statistics');
    }
  };

  // Helper Functions
  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      event: '',
      discountType: 'FIXED',
      discountValue: '',
      minPurchaseAmount: 0,
      maxDiscountAmount: '',
      usageLimit: '',
      userUsageLimit: 1,
      validFrom: new Date().toISOString().split('T')[0],
      validUntil: '',
      isActive: true
    });
    setSelectedCoupon(null);
  };

  const openEditModal = (coupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description || '',
      event: coupon.event?._id || coupon.event || '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue.toString(),
      minPurchaseAmount: coupon.minPurchaseAmount || 0,
      maxDiscountAmount: coupon.maxDiscountAmount?.toString() || '',
      usageLimit: coupon.usageLimit?.toString() || '',
      userUsageLimit: coupon.userUsageLimit || 1,
      validFrom: new Date(coupon.validFrom).toISOString().split('T')[0],
      validUntil: new Date(coupon.validUntil).toISOString().split('T')[0],
      isActive: coupon.isActive
    });
    setIsModalOpen(true);
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('success', 'Coupon code copied!');
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setCurrentPage(1);
  };

  // Loading State
  if (loading && coupons.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FEF8F6]">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#F4A696] mx-auto mb-4 h-12 w-12" />
          <p className="text-lg font-semibold text-[#1B3C5C]">Loading coupons...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent p-1 sm:p-0 md:p-0">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1B3C5C] flex items-center gap-2 sm:gap-3">
              <Ticket className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-[#F4A696]" />
              Coupon Management
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Create and manage discount coupons for your events
            </p>
          </div>

          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F4A696] to-[#E89580] px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base text-white font-semibold shadow-md hover:shadow-lg transition-all whitespace-nowrap"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Create Coupon</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#F4A696]/20 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-600 font-medium uppercase truncate">
                  Total Coupons
                </p>
                <p className="text-xl sm:text-2xl font-bold text-[#1B3C5C] mt-1">
                  {statsData.total}
                </p>
              </div>
              <div className="bg-blue-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                <Ticket className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#F4A696]/20 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-600 font-medium uppercase truncate">
                  Active Coupons
                </p>
                <p className="text-xl sm:text-2xl font-bold text-green-600 mt-1">
                  {statsData.active}
                </p>
              </div>
              <div className="bg-green-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                <CheckCircle className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#F4A696]/20 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-600 font-medium uppercase truncate">
                  Expired
                </p>
                <p className="text-xl sm:text-2xl font-bold text-red-600 mt-1">
                  {statsData.expired}
                </p>
              </div>
              <div className="bg-red-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#F4A696]/20 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-600 font-medium uppercase truncate">
                  Total Usage
                </p>
                <p className="text-xl sm:text-2xl font-bold text-[#F4A696] mt-1">
                  {statsData.totalUsage}
                </p>
              </div>
              <div className="bg-[#FFF4F0] p-2 sm:p-3 rounded-lg flex-shrink-0">
                <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 text-[#F4A696]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#F4A696]/20 shadow-sm mb-4 sm:mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative lg:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border border-gray-300 focus:border-[#F4A696] focus:ring-2 focus:ring-[#F4A696]/20 outline-none transition-all"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border border-gray-300 focus:border-[#F4A696] focus:ring-2 focus:ring-[#F4A696]/20 outline-none appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="valid">Valid</option>
              <option value="expired">Expired</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Type Filter */}
          <div className="relative sm:col-span-2 lg:col-span-1">
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border border-gray-300 focus:border-[#F4A696] focus:ring-2 focus:ring-[#F4A696]/20 outline-none appearance-none bg-white"
            >
              <option value="all">All Types</option>
              <option value="FIXED">Fixed Amount</option>
              <option value="PERCENTAGE">Percentage</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Active Filters Display */}
        {(searchTerm || filterStatus !== 'all' || filterType !== 'all') && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
            <span className="text-xs sm:text-sm text-gray-600 font-medium">Active Filters:</span>

            {searchTerm && (
              <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg text-xs sm:text-sm font-medium">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-blue-900">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {filterStatus !== 'all' && (
              <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-lg text-xs sm:text-sm font-medium">
                Status: {filterStatus}
                <button onClick={() => setFilterStatus('all')} className="hover:text-green-900">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {filterType !== 'all' && (
              <span className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 px-2.5 py-1 rounded-lg text-xs sm:text-sm font-medium">
                Type: {filterType}
                <button onClick={() => setFilterType('all')} className="hover:text-purple-900">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            <button
              onClick={clearAllFilters}
              className="ml-auto text-xs sm:text-sm text-[#F4A696] hover:text-[#E89580] font-medium"
            >
              Clear All
            </button>
          </div>
        )}

        {/* Results Count */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs sm:text-sm text-gray-600">
            Showing <span className="font-semibold text-[#1B3C5C]">{coupons.length}</span> of{' '}
            <span className="font-semibold text-[#1B3C5C]">{totalCoupons}</span>{' '}
            {totalCoupons === 1 ? 'coupon' : 'coupons'}
          </p>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[#F4A696]" />}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white rounded-xl border border-[#F4A696]/20 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#FFF4F0] to-white border-b border-[#F4A696]/20">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#1B3C5C] uppercase tracking-wider">
                  Coupon Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#1B3C5C] uppercase tracking-wider">
                  Name / Event
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#1B3C5C] uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#1B3C5C] uppercase tracking-wider">
                  Valid Until
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#1B3C5C] uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#1B3C5C] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-[#1B3C5C] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {coupons.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Ticket className="h-16 w-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">No coupons found</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                          ? 'Try adjusting your filters'
                          : 'Create your first coupon to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                coupons.map((coupon) => (
                  <tr key={coupon._id} className="hover:bg-[#FFF4F0]/30 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <code className="bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white px-3 py-1.5 rounded-lg font-mono text-sm font-bold">
                          {coupon.code}
                        </code>
                        <button
                          onClick={() => copyToClipboard(coupon.code)}
                          className="text-gray-400 hover:text-[#F4A696] transition-colors"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <p className="font-medium text-[#1B3C5C]">{coupon.name}</p>
                      {coupon.event && (
                        <p className="text-xs text-gray-500 mt-1">
                          📍 {coupon.event.title || 'Event'}
                        </p>
                      )}
                      {coupon.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                          {coupon.description}
                        </p>
                      )}
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {coupon.discountType === 'PERCENTAGE' ? (
                          <Percent className="h-4 w-4 text-green-600" />
                        ) : (
                          <DollarSign className="h-4 w-4 text-blue-600" />
                        )}
                        <span className="font-semibold text-[#1B3C5C]">
                          {coupon.discountType === 'PERCENTAGE'
                            ? `${coupon.discountValue}%`
                            : `₹${coupon.discountValue}`}
                        </span>
                      </div>
                      {coupon.minPurchaseAmount > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Min: ₹{coupon.minPurchaseAmount}
                        </p>
                      )}
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {new Date(coupon.validUntil).toLocaleDateString()}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-[#1B3C5C]">
                          {coupon.usageCount || 0}
                          {coupon.usageLimit && ` / ${coupon.usageLimit}`}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold w-fit ${
                            coupon.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${
                              coupon.isActive ? 'bg-green-600' : 'bg-gray-400'
                            }`}
                          />
                          {coupon.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {coupon.isExpired && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 w-fit">
                            Expired
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewStats(coupon._id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Statistics"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleToggleStatus(coupon._id)}
                          className={`p-2 rounded-lg transition-colors ${
                            coupon.isActive
                              ? 'text-gray-600 hover:bg-gray-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={coupon.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {coupon.isActive ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>

                        <button
                          onClick={() => openEditModal(coupon)}
                          className="p-2 text-[#F4A696] hover:bg-[#FFF4F0] rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-100 px-4 py-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || loading}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || loading}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white text-sm font-medium hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {coupons.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-[#F4A696]/20">
            <Ticket className="h-16 w-16 text-gray-300 mb-4 mx-auto" />
            <p className="text-gray-500 font-medium">No coupons found</p>
            <p className="text-sm text-gray-400 mt-1">
              {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first coupon to get started'}
            </p>
          </div>
        ) : (
          coupons.map((coupon) => (
            <div
              key={coupon._id}
              className="bg-white rounded-xl p-4 border border-[#F4A696]/20 shadow-sm"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0 mr-2">
                  <div className="flex items-center gap-2 mb-2">
                    <code className="bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white px-3 py-1.5 rounded-lg font-mono text-sm font-bold">
                      {coupon.code}
                    </code>
                    <button
                      onClick={() => copyToClipboard(coupon.code)}
                      className="text-gray-400 hover:text-[#F4A696] transition-colors flex-shrink-0"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="font-semibold text-[#1B3C5C] text-sm">{coupon.name}</p>
                  {coupon.event && (
                    <p className="text-xs text-gray-500 mt-1">
                      📍 {coupon.event.title || 'Event'}
                    </p>
                  )}
                  {coupon.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {coupon.description}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1 flex-shrink-0">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      coupon.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        coupon.isActive ? 'bg-green-600' : 'bg-gray-400'
                      }`}
                    />
                    {coupon.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {coupon.isExpired && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Expired
                    </span>
                  )}
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Discount</p>
                  <div className="flex items-center gap-1.5">
                    {coupon.discountType === 'PERCENTAGE' ? (
                      <Percent className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <DollarSign className="h-3.5 w-3.5 text-blue-600" />
                    )}
                    <span className="text-sm font-bold text-[#1B3C5C]">
                      {coupon.discountType === 'PERCENTAGE'
                        ? `${coupon.discountValue}%`
                        : `₹${coupon.discountValue}`}
                    </span>
                  </div>
                  {coupon.minPurchaseAmount > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Min: ₹{coupon.minPurchaseAmount}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">Usage</p>
                  <div className="flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-sm font-bold text-[#1B3C5C]">
                      {coupon.usageCount || 0}
                      {coupon.usageLimit && ` / ${coupon.usageLimit}`}
                    </span>
                  </div>
                </div>

                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Valid Until</p>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-sm text-gray-700">
                      {new Date(coupon.validUntil).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => handleViewStats(coupon._id)}
                  className="flex flex-col items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Stats"
                >
                  <BarChart3 className="h-4 w-4 mb-1" />
                  <span className="text-xs">Stats</span>
                </button>

                <button
                  onClick={() => handleToggleStatus(coupon._id)}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                    coupon.isActive
                      ? 'text-gray-600 hover:bg-gray-50'
                      : 'text-green-600 hover:bg-green-50'
                  }`}
                  title={coupon.isActive ? 'Deactivate' : 'Activate'}
                >
                  {coupon.isActive ? (
                    <EyeOff className="h-4 w-4 mb-1" />
                  ) : (
                    <Eye className="h-4 w-4 mb-1" />
                  )}
                  <span className="text-xs">{coupon.isActive ? 'Hide' : 'Show'}</span>
                </button>

                <button
                  onClick={() => openEditModal(coupon)}
                  className="flex flex-col items-center justify-center p-2 text-[#F4A696] hover:bg-[#FFF4F0] rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 className="h-4 w-4 mb-1" />
                  <span className="text-xs">Edit</span>
                </button>

                <button
                  onClick={() => handleDelete(coupon._id)}
                  className="flex flex-col items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4 mb-1" />
                  <span className="text-xs">Delete</span>
                </button>
              </div>
            </div>
          ))
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl p-4 border border-[#F4A696]/20 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
                className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white text-sm font-medium hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal - UPDATED */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-[#F4A696]/20">
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-[#F4A696]/20 bg-gradient-to-r from-[#F4A696] to-[#E89580]">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  {selectedCoupon ? (
                    <Edit2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                  {selectedCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="space-y-4 sm:space-y-5">
                {/* UPDATED: Event Selection - Shows only title and date */}
                <div className="bg-gradient-to-r from-purple-50 to-white p-3 sm:p-4 rounded-xl border border-purple-200/50">
                  <h3 className="text-sm font-bold text-[#1B3C5C] mb-3 sm:mb-4 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-purple-600" />
                    Event Selection
                  </h3>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                      Select Event *
                    </label>
                    <div className="relative">
                      <select
                        value={formData.event}
                        onChange={(e) => handleEventChange(e.target.value)}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all appearance-none bg-white"
                        required
                      >
                        <option value="">-- Select an event --</option>
                        {events.map((event) => (
                          <option key={event._id} value={event._id}>
                            {event.title} • {new Date(event.date).toLocaleDateString('en-IN', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 pointer-events-none" />
                    </div>
                    <p className="text-xs text-purple-600 mt-2 flex items-start gap-1">
                      <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>This coupon will be valid only for the selected event. Valid Until date will auto-fill with event date.</span>
                    </p>
                  </div>
                </div>

                {/* Row 1: Code & Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-[#1B3C5C] mb-2">
                      Coupon Code *
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) =>
                        setFormData({ ...formData, code: e.target.value.toUpperCase() })
                      }
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-mono uppercase"
                      placeholder="e.g., SUMMER2024"
                      required
                      disabled={!!selectedCoupon}
                      maxLength={20}
                      minLength={3}
                    />
                    <p className="text-xs text-gray-500 mt-1">3-20 characters, letters and numbers</p>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#1B3C5C] mb-2">
                      Coupon Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all"
                      placeholder="e.g., Summer Sale"
                      required
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="2"
                    maxLength={500}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all resize-none"
                    placeholder="Optional coupon description..."
                  />
                </div>

                {/* Discount Configuration */}
                <div className="bg-gradient-to-r from-[#FFF4F0] to-white p-3 sm:p-4 rounded-xl border border-[#F4A696]/20">
                  <h3 className="text-sm font-bold text-[#1B3C5C] mb-3 sm:mb-4">
                    Discount Configuration
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                        Discount Type *
                      </label>
                      <div className="relative">
                        <select
                          value={formData.discountType}
                          onChange={(e) =>
                            setFormData({ ...formData, discountType: e.target.value })
                          }
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all appearance-none bg-white"
                        >
                          <option value="FIXED">Fixed Amount (₹)</option>
                          <option value="PERCENTAGE">Percentage (%)</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                        Discount Value *
                      </label>
                      <div className="relative">
                        {formData.discountType === 'PERCENTAGE' ? (
                          <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-[#F4A696]" />
                        ) : (
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F4A696] font-bold">₹</span>
                        )}
                        <input
                          type="number"
                          value={formData.discountValue}
                          onChange={(e) =>
                            setFormData({ ...formData, discountValue: e.target.value })
                          }
                          min="0"
                          max={formData.discountType === 'PERCENTAGE' ? '100' : undefined}
                          step="0.01"
                          className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all"
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                        Minimum Purchase Amount
                      </label>
                      <input
                        type="number"
                        value={formData.minPurchaseAmount}
                        onChange={(e) =>
                          setFormData({ ...formData, minPurchaseAmount: e.target.value })
                        }
                        min="0"
                        step="0.01"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all"
                        placeholder="0.00"
                      />
                    </div>

                    {formData.discountType === 'PERCENTAGE' && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                          Maximum Discount Amount
                        </label>
                        <input
                          type="number"
                          value={formData.maxDiscountAmount}
                          onChange={(e) =>
                            setFormData({ ...formData, maxDiscountAmount: e.target.value })
                          }
                          min="0"
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all"
                          placeholder="Optional cap"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Usage Limits */}
                <div className="bg-gradient-to-r from-blue-50 to-white p-3 sm:p-4 rounded-xl border border-blue-200/50">
                  <h3 className="text-sm font-bold text-[#1B3C5C] mb-3 sm:mb-4">Usage Limits</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                        Total Usage Limit
                      </label>
                      <input
                        type="number"
                        value={formData.usageLimit}
                        onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                        min="1"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="Unlimited"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                        Per User Limit *
                      </label>
                      <input
                        type="number"
                        value={formData.userUsageLimit}
                        onChange={(e) =>
                          setFormData({ ...formData, userUsageLimit: e.target.value })
                        }
                        min="1"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Validity Period - UPDATED */}
                <div className="bg-gradient-to-r from-green-50 to-white p-3 sm:p-4 rounded-xl border border-green-200/50">
                  <h3 className="text-sm font-bold text-[#1B3C5C] mb-3 sm:mb-4">Validity Period</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                        Valid From *
                      </label>
                      <input
                        type="date"
                        value={formData.validFrom}
                        onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-[#1B3C5C] mb-2">
                        Valid Until * 
                        {formData.event && (
                          <span className="text-xs font-normal text-green-600 ml-1">(Auto-filled from event)</span>
                        )}
                      </label>
                      <input
                        type="date"
                        value={formData.validUntil}
                        onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                        min={formData.validFrom}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Coupon will be valid until the end of this date
                      </p>
                    </div>
                  </div>
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3 bg-[#FFF4F0] px-3 sm:px-4 py-3 rounded-xl border border-[#F4A696]/20">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 sm:w-5 sm:h-5 text-[#F4A696] border-2 border-[#F4A696]/30 rounded focus:ring-[#F4A696]"
                  />
                  <label htmlFor="isActive" className="text-sm font-bold text-[#1B3C5C]">
                    Coupon is Active
                  </label>
                </div>
              </div>
            </form>

            {/* Modal Footer */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-[#F4A696]/20 bg-gradient-to-r from-[#FFF4F0] to-white">
              <div className="flex items-center justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-[#F4A696]/30 rounded-xl hover:bg-[#FFF4F0] transition-all font-bold text-[#1B3C5C]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-xl hover:from-[#E89580] hover:to-[#F4A696] disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-md hover:shadow-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      <span>{selectedCoupon ? 'Updating...' : 'Creating...'}</span>
                    </>
                  ) : (
                    <>
                      {selectedCoupon ? (
                        <>
                          <Save className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="hidden sm:inline">Update Coupon</span>
                          <span className="sm:hidden">Update</span>
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="hidden sm:inline">Create Coupon</span>
                          <span className="sm:hidden">Create</span>
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal - Keep as is */}
      {isStatsModalOpen && stats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-[#F4A696]/20">
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-[#F4A696]/20 bg-gradient-to-r from-blue-500 to-blue-600">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />
                  Coupon Statistics
                </h2>
                <button
                  onClick={() => setIsStatsModalOpen(false)}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-white p-3 sm:p-4 rounded-xl border border-blue-200">
                  <p className="text-xs text-gray-600 font-medium uppercase mb-2">Total Uses</p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.totalUsages}</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white p-3 sm:p-4 rounded-xl border border-green-200">
                  <p className="text-xs text-gray-600 font-medium uppercase mb-2">Remaining</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">
                    {stats.remainingUsages}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#F4A696]/10 to-white p-3 sm:p-4 rounded-xl border border-[#F4A696]/20">
                  <p className="text-xs text-gray-600 font-medium uppercase mb-2">Total Savings</p>
                  <p className="text-xl sm:text-2xl font-bold text-[#F4A696]">
                    ₹{stats.totalDiscount?.toFixed(2) || 0}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-white p-3 sm:p-4 rounded-xl border border-purple-200">
                  <p className="text-xs text-gray-600 font-medium uppercase mb-2">Unique Users</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.uniqueUsers}</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white p-3 sm:p-4 rounded-xl border border-orange-200">
                <p className="text-xs text-gray-600 font-medium uppercase mb-2">
                  Average Discount Per Use
                </p>
                <p className="text-lg sm:text-xl font-bold text-orange-600">
                  ₹{stats.avgDiscountPerUse?.toFixed(2) || 0}
                </p>
              </div>

              {stats.recentUsage && stats.recentUsage.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-[#1B3C5C] mb-3">Recent Usage</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {stats.recentUsage.map((usage, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 mr-2">
                            <p className="text-sm font-medium text-[#1B3C5C] truncate">
                              {usage.user?.firstName || 'Unknown'} {usage.user?.lastName || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{usage.user?.email}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-green-600">
                              -₹{usage.discountAmount}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(usage.usedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setIsStatsModalOpen(false)}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-lg font-medium hover:shadow-md transition-all text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] max-w-sm">
          <div
            className={`flex items-start gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-2xl border-2 ${
              notification.type === 'success'
                ? 'bg-white border-green-300 text-green-800'
                : notification.type === 'error'
                ? 'bg-white border-red-300 text-red-800'
                : 'bg-white border-blue-300 text-blue-800'
            }`}
          >
            <div className="flex-shrink-0 pt-0.5">
              {notification.type === 'success' ? (
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              ) : (
                <AlertCircle
                  className={`h-5 w-5 sm:h-6 sm:w-6 ${
                    notification.type === 'error' ? 'text-red-600' : 'text-blue-600'
                  }`}
                />
              )}
            </div>
            <p className="font-bold text-sm sm:text-base flex-1">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="text-gray-500 hover:text-gray-700 flex-shrink-0"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupon;
